import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api";
import { haptic } from "../telegram";
import type { LikeItem } from "../types";

type Mode = "incoming" | "outgoing";

export function Likes({ onMatched, isDiscoverable }: { onMatched(): void; isDiscoverable: boolean }) {
  const [mode, setMode] = useState<Mode>("incoming");
  const [items, setItems] = useState<LikeItem[] | null>(null);
  const [underReview, setUnderReview] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(
    async (nextMode: Mode = mode) => {
      setItems(null);
      setUnderReview(false);
      try {
        const result = await api<{ likes: LikeItem[] }>(`/v1/likes/${nextMode}?limit=30`);
        setItems(result.likes);
      } catch (error) {
        if (error instanceof ApiError && error.code === "profile_under_review") {
          setUnderReview(true);
        }
        setItems([]);
      }
    },
    [mode],
  );

  useEffect(() => {
    void load(mode);
  }, [load, mode]);

  const changeMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
  };

  const likeBack = async (item: LikeItem) => {
    if (busyId || !isDiscoverable) return;
    setBusyId(item.id);
    try {
      const result = await api<{ matched: boolean }>(`/v1/discovery/${item.id}/swipes`, {
        method: "POST",
        body: JSON.stringify({ decision: "like" }),
      });
      haptic(result.matched ? "success" : "like");
      setItems((current) => current?.filter((like) => like.id !== item.id) ?? []);
      if (result.matched) onMatched();
    } finally {
      setBusyId(null);
    }
  };

  if (underReview)
    return (
      <section className="review-gate">
        <span className="review-icon">⌛</span>
        <h2>Профиль ещё проверяется</h2>
        <p>Лайки откроются после одобрения главного фото.</p>
      </section>
    );

  return (
    <section className="content-page likes-page">
      <div className="section-heading page-heading">
        <span className="eyebrow">Симпатии</span>
        <h1>Лайки</h1>
      </div>

      {!isDiscoverable && (
        <p className="likes-visibility-note" role="status">
          Анкета скрыта: старые симпатии можно просмотреть, но ответить лайком получится после включения видимости.
        </p>
      )}
      <div className="segmented-control" role="tablist" aria-label="Лайки">
        <button type="button" className={mode === "incoming" ? "active" : ""} onClick={() => changeMode("incoming")}>
          Кто меня лайкнул
        </button>
        <button type="button" className={mode === "outgoing" ? "active" : ""} onClick={() => changeMode("outgoing")}>
          Кого я лайкнул
        </button>
      </div>

      {items === null ? (
        <div className="list-skeleton" />
      ) : items.length === 0 ? (
        <section className="empty-state inline-empty">
          <div className="empty-symbol">{mode === "incoming" ? "♡" : "↗"}</div>
          <h2>{mode === "incoming" ? "Новых лайков пока нет" : "Вы пока никого не лайкнули"}</h2>
          <p>
            {mode === "incoming"
              ? "Когда кому-то понравится ваша анкета, профиль появится здесь."
              : "Лайкнутые анкеты будут ждать ответа в этом разделе."}
          </p>
        </section>
      ) : (
        <div className="likes-grid">
          {items.map((item, index) => (
            <article className="like-card" key={item.id} style={{ animationDelay: `${Math.min(index, 10) * 30}ms` }}>
              <img src={item.photoUrl} alt={`Фото: ${item.displayName}`} />
              <div className="like-card-shade" />
              <div className="like-card-copy">
                <strong>
                  {item.displayName}
                  {item.age !== null ? `, ${item.age}` : ""}
                </strong>
                <span>
                  {item.isSuperlike ? "✦ Суперлайк · " : ""}
                  {item.city}
                </span>
              </div>
              {mode === "incoming" ? (
                <button
                  type="button"
                  className="like-back-button"
                  disabled={busyId === item.id || !isDiscoverable}
                  onClick={() => void likeBack(item)}
                >
                  {busyId === item.id ? "…" : "♥"}
                </button>
              ) : (
                <span className="waiting-pill">Ждём ответа</span>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
